import { useState, useEffect } from 'react';

// Available bin sizes with floor space requirements
const BIN_SPECS = {
  120: { capacity: 120, floorSpace: 0.27, label: '120L (Small)' },
  240: { capacity: 240, floorSpace: 0.43, label: '240L (Standard)' },
  660: { capacity: 660, floorSpace: 1.16, label: '660L (Medium)' },
  1100: { capacity: 1100, floorSpace: 1.71, label: '1100L (Large)' },
  3000: { capacity: 3000, floorSpace: 3.60, label: '3000L (Bulk)' },
};

const BIN_SIZES = [120, 240, 660, 1100, 3000];

// Single stream configurator
const StreamConfigurator = ({ 
  streamName, 
  streamLabel, 
  requiredCapacity, 
  collectionsPerWeek,
  binConfig,
  onChange,
  color = 'blue'
}) => {
  const getTotalCapacity = () => {
    return Object.entries(binConfig).reduce((total, [size, count]) => {
      return total + (parseInt(size) * count * collectionsPerWeek);
    }, 0);
  };

  const getTotalBins = () => {
    return Object.values(binConfig).reduce((sum, count) => sum + count, 0);
  };

  const getFloorSpace = () => {
    return Object.entries(binConfig).reduce((total, [size, count]) => {
      return total + (BIN_SPECS[size].floorSpace * count);
    }, 0);
  };

  const meetsRequirement = getTotalCapacity() >= requiredCapacity;
  const totalCapacity = getTotalCapacity();
  const totalBins = getTotalBins();
  const floorSpace = getFloorSpace();

  const updateBinCount = (size, count) => {
    const newConfig = { ...binConfig, [size]: Math.max(0, count) };
    onChange(streamName, newConfig);
  };

  const colorClasses = {
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', header: 'bg-blue-100' },
    green: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', header: 'bg-green-100' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', header: 'bg-amber-100' },
  };

  const colors = colorClasses[color] || colorClasses.blue;

  return (
    <div className={`border ${colors.border} rounded-lg overflow-hidden`}>
      {/* Header */}
      <div className={`${colors.header} px-4 py-2 flex justify-between items-center`}>
        <span className={`font-medium ${colors.text}`}>{streamLabel}</span>
        <span className="text-sm text-gray-600">
          Required: {Math.ceil(requiredCapacity).toLocaleString()}L/week
        </span>
      </div>

      {/* Bin Selection Grid */}
      <div className={`${colors.bg} p-4`}>
        <div className="grid grid-cols-5 gap-2 mb-4">
          {BIN_SIZES.map(size => (
            <div key={size} className="text-center">
              <label className="block text-xs text-gray-500 mb-1">
                {BIN_SPECS[size].label}
              </label>
              <div className="flex items-center justify-center">
                <button
                  type="button"
                  onClick={() => updateBinCount(size, (binConfig[size] || 0) - 1)}
                  className="w-6 h-8 flex items-center justify-center bg-white border border-gray-300 rounded-l hover:bg-gray-100 text-gray-600"
                  disabled={!binConfig[size]}
                >
                  -
                </button>
                <input
                  type="number"
                  min="0"
                  value={binConfig[size] || 0}
                  onChange={(e) => updateBinCount(size, parseInt(e.target.value) || 0)}
                  className="w-10 h-8 text-center border-t border-b border-gray-300 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => updateBinCount(size, (binConfig[size] || 0) + 1)}
                  className="w-6 h-8 flex items-center justify-center bg-white border border-gray-300 rounded-r hover:bg-gray-100 text-gray-600"
                >
                  +
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Totals Row */}
        <div className={`flex items-center justify-between p-2 rounded ${meetsRequirement ? 'bg-green-100' : 'bg-red-100'}`}>
          <div className="flex gap-6 text-sm">
            <span>
              <strong>{totalBins}</strong> bins
            </span>
            <span>
              <strong>{totalCapacity.toLocaleString()}L</strong>/week capacity
            </span>
            <span>
              <strong>{floorSpace.toFixed(2)}</strong> m² floor space
            </span>
          </div>
          <span className={`text-sm font-medium ${meetsRequirement ? 'text-green-700' : 'text-red-700'}`}>
            {meetsRequirement ? '✓ Meets requirement' : `✗ Need ${Math.ceil(requiredCapacity - totalCapacity).toLocaleString()}L more`}
          </span>
        </div>
      </div>
    </div>
  );
};

const CustomBinConfigurator = ({ 
  wasteGeneration, 
  collectionsPerWeek = 1,
  onConfigChange,
  initialConfig = null,
  label = "Custom Configuration",
  thirdStreamLabel = "Organics / FOGO"  // Can be "Clinical Waste" for medical facilities
}) => {
  const [customBins, setCustomBins] = useState({
    general_waste: { 120: 0, 240: 0, 660: 0, 1100: 0, 3000: 0 },
    recycling: { 120: 0, 240: 0, 660: 0, 1100: 0, 3000: 0 },
    organics: { 120: 0, 240: 0, 660: 0, 1100: 0, 3000: 0 },
  });

  // Initialize from saved config
  useEffect(() => {
    if (initialConfig?.custom_bins) {
      setCustomBins(initialConfig.custom_bins);
    }
  }, [initialConfig]);

  const handleStreamChange = (streamName, binConfig) => {
    const newConfig = { ...customBins, [streamName]: binConfig };
    setCustomBins(newConfig);
    
    // Calculate totals and notify parent
    const totals = calculateTotals(newConfig);
    onConfigChange({
      is_custom: true,
      custom_bins: newConfig,
      ...totals
    });
  };

  const calculateTotals = (config) => {
    let totalBins = 0;
    let totalFloorSpace = 0;
    const streams = {};

    for (const [stream, bins] of Object.entries(config)) {
      let streamBins = 0;
      let streamFloorSpace = 0;
      let streamCapacity = 0;
      const binBreakdown = [];

      for (const [size, count] of Object.entries(bins)) {
        if (count > 0) {
          streamBins += count;
          streamFloorSpace += BIN_SPECS[size].floorSpace * count;
          streamCapacity += parseInt(size) * count * collectionsPerWeek;
          binBreakdown.push({ size: parseInt(size), count });
        }
      }

      totalBins += streamBins;
      totalFloorSpace += streamFloorSpace;
      
      // Get weekly generation from wasteGeneration prop
      const weeklyGen = wasteGeneration?.[stream] || wasteGeneration?.[stream.replace('_', '')] || 0;
      
      streams[stream] = {
        bins_needed: streamBins,
        weekly_capacity: streamCapacity,
        weekly_generation: weeklyGen,
        collections_per_week: collectionsPerWeek,
        floor_space: streamFloorSpace,
        bin_breakdown: binBreakdown
      };
    }

    return {
      total_bins: totalBins,
      total_floor_space: totalFloorSpace,
      storage_area: totalFloorSpace * 1.2, // 20% circulation
      streams
    };
  };

  const isValid = () => {
    const gwCapacity = Object.entries(customBins.general_waste).reduce(
      (sum, [size, count]) => sum + (parseInt(size) * count * collectionsPerWeek), 0
    );
    const recCapacity = Object.entries(customBins.recycling).reduce(
      (sum, [size, count]) => sum + (parseInt(size) * count * collectionsPerWeek), 0
    );
    const orgCapacity = Object.entries(customBins.organics).reduce(
      (sum, [size, count]) => sum + (parseInt(size) * count * collectionsPerWeek), 0
    );

    return gwCapacity >= wasteGeneration.general_waste &&
           recCapacity >= wasteGeneration.recycling &&
           orgCapacity >= wasteGeneration.organics;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-900">{label}</h4>
        <span className="text-sm text-gray-500">
          Collections: {collectionsPerWeek}x per week
        </span>
      </div>

      <StreamConfigurator
        streamName="general_waste"
        streamLabel="General Waste"
        requiredCapacity={wasteGeneration.general_waste}
        collectionsPerWeek={collectionsPerWeek}
        binConfig={customBins.general_waste}
        onChange={handleStreamChange}
        color="blue"
      />

      <StreamConfigurator
        streamName="recycling"
        streamLabel="Recycling"
        requiredCapacity={wasteGeneration.recycling}
        collectionsPerWeek={collectionsPerWeek}
        binConfig={customBins.recycling}
        onChange={handleStreamChange}
        color="green"
      />

      <StreamConfigurator
        streamName="organics"
        streamLabel={thirdStreamLabel}
        requiredCapacity={wasteGeneration.organics}
        collectionsPerWeek={collectionsPerWeek}
        binConfig={customBins.organics}
        onChange={handleStreamChange}
        color="amber"
      />

      {/* Overall Summary */}
      <div className={`p-4 rounded-lg ${isValid() ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
        <div className="flex justify-between items-center">
          <div className="space-y-1">
            <p className="text-sm font-medium text-gray-900">
              Total: {calculateTotals(customBins).total_bins} bins
            </p>
            <p className="text-xs text-gray-600">
              Floor space: {calculateTotals(customBins).total_floor_space.toFixed(2)} m² 
              (+ 20% circulation = {calculateTotals(customBins).storage_area.toFixed(2)} m²)
            </p>
          </div>
          {isValid() ? (
            <span className="text-green-700 font-medium text-sm flex items-center">
              <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Configuration Valid
            </span>
          ) : (
            <span className="text-amber-700 font-medium text-sm flex items-center">
              <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Insufficient Capacity
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomBinConfigurator;
